import React from 'react';
import { Box, MenuItem, Select, Typography } from '@mui/material';

import { GT_COLORS } from '../groups-teams/shared/constants';

/**
 * A dropdown whose options explain themselves when it is open, and show only
 * their label when it is closed.
 *
 * This is where the Notifications page keeps its explanations: at the moment
 * someone is choosing, one sentence per option, and out of sight the rest of
 * the time. Anything an admin needs to read to choose correctly belongs in an
 * option's description, not in a tooltip or a paragraph on the page.
 */

export interface DescribedOption {
    value: string;
    label: string;
    description: string;
    /** A short tag beside the label in the open menu, such as "Recommended". */
    badge?: string;
}

interface Props {
    value: string;
    options: DescribedOption[];
    ariaLabel: string;
    disabled?: boolean;
    minWidth?: number;
    onChange: (value: string) => void;
}

export const DescribedSelect: React.FC<Props> = ({
    value,
    options,
    ariaLabel,
    disabled,
    minWidth = 200,
    onChange,
}) => (
    <Select
        size="small"
        value={value}
        disabled={disabled}
        inputProps={{ 'aria-label': ariaLabel }}
        onChange={(e) => onChange(String(e.target.value))}
        renderValue={(selected) => options.find((o) => o.value === selected)?.label ?? selected}
        MenuProps={{
            // Drop straight down from the field's left edge. By default MUI
            // lines the selected option up over the field, which pushes a menu
            // wider than the field off to the left.
            anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
            transformOrigin: { vertical: 'top', horizontal: 'left' },
            PaperProps: { sx: { maxWidth: 380, marginTop: '4px' } },
        }}
        sx={{ minWidth, fontSize: '14px' }}
    >
        {options.map((option) => (
            <MenuItem key={option.value} value={option.value} sx={{ whiteSpace: 'normal', alignItems: 'flex-start', py: '10px' }}>
                <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Typography sx={{ fontSize: '14px', fontWeight: 500 }}>{option.label}</Typography>
                        {option.badge && (
                            <Box component="span" sx={{
                                fontSize: '11px',
                                fontWeight: 600,
                                padding: '1px 8px',
                                borderRadius: '999px',
                                backgroundColor: GT_COLORS.greenLight,
                                color: GT_COLORS.green,
                            }}>
                                {option.badge}
                            </Box>
                        )}
                    </Box>
                    <Typography sx={{ fontSize: '12px', color: GT_COLORS.textMuted, lineHeight: 1.45 }}>
                        {option.description}
                    </Typography>
                </Box>
            </MenuItem>
        ))}
    </Select>
);
